// useTableFields.js

import { useMemo } from 'react';

import { isCatchAllView } from 'utilities/ViewRows';
import { filterDisplayFieldKeys, resolveDisplaySchemaFields } from 'views/utils/displaySchemaFields';

import {
    buildStandardFieldMap,
    getDataSectionName,
} from 'views/table/utils/tableFieldUtils';

const useTableFields = ({ enforceExplicitProperties = false, typeDef, view, nodeTypesRevision }) => {
    const fields = useMemo(() => {
        void typeDef?.dateModified;
        void nodeTypesRevision;

        // Respect Admin Visible / Hidden — same as forms and list items.
        return resolveDisplaySchemaFields(typeDef, { includeHidden: false });
    }, [
        typeDef,
        nodeTypesRevision,
    ]);

    const hasExplicitProperties = Array.isArray(view?.properties) && view.properties.length > 0;
    const catchAllView = isCatchAllView(view);

    const schemaFieldMap = useMemo(() => {
        const map = buildStandardFieldMap();
        fields.forEach((field) => {
            const key = field?.name || field?.key;
            if (key) map.set(key, field);
        });
        return map;
    }, [fields]);

    const fieldKeys = useMemo(() => {
        const input = Array.isArray(view?.properties) ? view.properties : null;
        const seen = new Set();

        if (enforceExplicitProperties && (!input || input.length === 0)) {
            return [];
        }

        if (catchAllView) {
            if (Array.isArray(input)) {
                if (input.length === 0) return [];
                const next = [];
                input.forEach((field) => {
                    const key = String(field || '').trim();
                    if (!key || key === 'title' || seen.has(key)) return;
                    seen.add(key);
                    next.push(key);
                });
                return filterDisplayFieldKeys(next, schemaFieldMap);
            }

            const schemaKeys = fields
                .map((field) => field?.name || field?.key)
                .filter((key) => key && key !== 'title');

            const ordered = [
                'autonumber',
                'autokey',
                ...schemaKeys,
                'dateCreated',
                'dateModified',
            ];

            const filtered = filterDisplayFieldKeys(ordered, schemaFieldMap)
                .filter((key) => {
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                });

            return filtered;
        }

        if (!input || input.length === 0) {
            const keys = fields
                .map((field) => field?.name || field?.key)
                .filter((key) => key && key !== 'title')
                .filter((key) => {
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                });

            return filterDisplayFieldKeys(keys, schemaFieldMap);
        }

        const next = [];
        input.forEach((field) => {
            const key = String(field || '').trim();
            if (!key || key === 'title' || seen.has(key)) return;
            seen.add(key);
            next.push(key);
        });
        return filterDisplayFieldKeys(next, schemaFieldMap);
    }, [catchAllView, enforceExplicitProperties, fields, schemaFieldMap, view?.properties]);

    const visibleFieldKeys = useMemo(() => (
        fieldKeys.filter((fieldKey) => schemaFieldMap.has(fieldKey))
    ), [fieldKeys, schemaFieldMap]);

    const fieldDataSection = useMemo(() => (
        typeDef?.dataSection || getDataSectionName(typeDef?.key)
    ), [typeDef?.dataSection, typeDef?.key]);

    return {
        fields,
        hasExplicitProperties,
        fieldKeys,
        schemaFieldMap,
        visibleFieldKeys,
        fieldDataSection,
    };
};

export default useTableFields;
